import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import ErrorBoundary from "../components/ErrorBoundary.jsx";
import { useFindProgress } from "../components/FindProgress";
import BeaconLocation from "../components/BeaconLocation";
import { currentActor } from "./beacons";
import { hasPoint, locationCaption } from "./shopMap";
import {
  freeBeaconNote, palletBeaconsSubline, palletLightWords, palletPartsText, palletRowCanFind, palletRowKind,
  palletRowLocation, palletRowPill, palletRowText, replacePallet, taskPalletBeacon, taskPalletLights,
} from "../features/queue/palletBeacons";

export const PALLET_BEACONS_TITLE = "Pallets for this task";

/**
 * The Laser Start popup for sales-order pallet beacons (2026-10-22), shown by startTask
 * (features/queue/taskActions) through ui.palletBeacons on the desktop and the phone. Like
 * stickLotPrompt it renders in its own React root inside an ErrorBoundary, as a react-bootstrap
 * Modal (aria-modal), so a scan is held back while it is open (ScanWedge).
 *
 * `data` = the start answer's `pallet_beacons` ({task_id, pallets}), or GET /tasks/{id}/pallet_beacons when
 * reopened from the task (components/TaskPalletsButton, 2026-10-26). Nobody scans anything: the popup says
 * which pallet to get and which beacon is flashing for it. Each pallet with a beacon has its own Find (it
 * lights that one beacon) and, where the shop map knows, where the beacon was last placed ("Show map"). A
 * new pallet no free beacon could go on says why (the API's free_note) and has Try again. Resolves once, on close:
 *   {lightsOff: true}   OK (the lights were asked to go off: POST .../pallet_beacons/lights)
 *   {lightsOff: false}  closed with X / Escape (the lights stop on their own when their time ends)
 */
export default function palletBeaconsPrompt({ taskId, data, reopened = false }) {
  return new Promise((resolve) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);
    let settled = false;

    function finish(value) {
      if (settled) return;
      settled = true;
      root.unmount();
      container.remove();
      resolve(value);
    }

    root.render(
      <ErrorBoundary
        fallback={(error) => (
          <div className="alert alert-danger m-3" role="alert" style={{ position: "fixed", zIndex: 2001, top: 0, left: 0, right: 0 }}>
            The pallet list hit an error: {(error && error.message) || String(error)}.{reopened ? "" : " The task has started."}
            <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={() => finish({ lightsOff: false })}>Close</button>
          </div>
        )}
      >
        <PalletBeaconsModal taskId={taskId} data={data} onClose={(v) => finish(v)} />
      </ErrorBoundary>
    );
  });
}

const PILL_COLORS = {
  NEW: { background: "#198754", color: "#fff" },
  FIND: { background: "#0d6efd", color: "#fff" },
  LABEL: { background: "#ffc107", color: "#212529" },
};

const styles = {
  row: { borderBottom: "1px solid var(--bs-border-color, #dee2e6)", padding: "10px 0" },
  rowHead: { display: "flex", gap: 10, alignItems: "flex-start" },
  pill: {
    display: "inline-block", minWidth: 52, textAlign: "center", borderRadius: 999, padding: "2px 8px",
    fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.5px", flexShrink: 0, marginTop: 2,
  },
  text: { flex: 1, minWidth: 0 },
  under: { marginLeft: 62, marginTop: 4 },
  parts: { fontSize: "0.85rem", color: "var(--bs-secondary-color, #6c757d)", fontFamily: "monospace" },
  light: { fontSize: "0.85rem", marginTop: 2 },
  linkBtn: { padding: 0, fontSize: "0.85rem", verticalAlign: "baseline" },
  where: { fontSize: "0.85rem", marginTop: 2 },
  buttons: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 },
};

/**
 * The pallet rows' state and actions, shared by the Laser Start popup and the task pallets panel at the stations
 * after the laser (components/TaskPalletsPanel, 2026-10-26): Light again / Find all, one pallet's Find, assign /
 * swap (Laser only), and each row's error and refusal note.
 */
export function usePalletRows(id, initialPallets) {
  const [pallets, setPallets] = useState(() => (Array.isArray(initialPallets) ? initialPallets : []));
  const [busy, setBusy] = useState(""); // 'off' | 'again' | `swap:<id>` | `assign:<id>` | `find:<id>`
  const [error, setError] = useState(""); // the popup's own line (OK / Light again)
  const [rowErrors, setRowErrors] = useState({}); // pallet_id -> sentence
  const [rowNotes, setRowNotes] = useState({}); // pallet_id -> why no beacon is free (a refused Try again)


  async function again() {
    setBusy("again");
    setError("");
    const r = await taskPalletLights(id, "again", { actor: currentActor() });
    setBusy("");
    if (r.ok && r.body && Array.isArray(r.body.pallets)) {
      setPallets(r.body.pallets);
      setRowErrors({});
      setRowNotes({});         // the answer's own free_note is newer than any refusal's
    } else if (!r.ok) {
      setError(r.message || "Couldn't light them again.");
    }
  }

  async function beaconAction(p, action) {
    setBusy(`${action}:${p.pallet_id}`);
    setRowErrors((e) => ({ ...e, [p.pallet_id]: "" }));
    setRowNotes((n) => ({ ...n, [p.pallet_id]: "" }));
    const r = await taskPalletBeacon(id, p.pallet_id, action, { actor: currentActor() });
    setBusy("");
    if (r.ok && r.body && r.body.pallet) {
      setPallets((ps) => replacePallet(ps, r.body.pallet));
    } else if (!r.ok) {
      let text = r.message || "That didn't work.";
      if (r.error === "no_free_beacon") {
        // a refused swap is rolled back: the pallet keeps its beacon
        text = action === "swap" && p.beacon_id
          ? `No other free beacon right now: ${p.beacon_id} stays on it.`
          : (r.message || "No free beacon right now: print a pallet label.");
      }
      setRowErrors((e) => ({ ...e, [p.pallet_id]: text }));
      if (r.free_note) setRowNotes((n) => ({ ...n, [p.pallet_id]: r.free_note }));
    }
  }

  // One pallet's Find: lights just its beacon again (POST .../lights {action: again, pallet_ids: [it]}).
  async function findOne(p) {
    setBusy(`find:${p.pallet_id}`);
    setRowErrors((e) => ({ ...e, [p.pallet_id]: "" }));
    const r = await taskPalletLights(id, "again", { palletIds: [p.pallet_id], actor: currentActor() });
    setBusy("");
    const fresh = r.ok && r.body && Array.isArray(r.body.pallets)
      ? r.body.pallets.find((x) => x && x.pallet_id === p.pallet_id) : null;
    if (fresh) {
      setPallets((ps) => replacePallet(ps, fresh));
    } else if (r.ok) {
      // the task's list no longer has it (sealed, shipped or moved since the popup opened)
      setRowErrors((e) => ({ ...e, [p.pallet_id]: `${p.short_id || "This pallet"} is no longer one of this task's pallets being packed.` }));
    } else {
      setRowErrors((e) => ({ ...e, [p.pallet_id]: r.message || "Couldn't light it." }));
    }
  }


  return { pallets, setPallets, busy, setBusy, error, setError, rowErrors, rowNotes, again, beaconAction, findOne };
}

export function PalletBeaconsModal({ taskId, data, onClose }) {
  const id = (data && data.task_id) || taskId;
  const { pallets, busy, setBusy, error, setError, rowErrors, rowNotes, again, beaconAction, findOne } =
    usePalletRows(id, data && data.pallets);

  const subline = palletBeaconsSubline({ task_id: id, pallets });
  const anyBeacon = pallets.some((p) => p && p.beacon_id);

  async function ok() {
    setBusy("off");
    setError("");
    // OK only turns the lights off. A failure never keeps the popup open: the lights end by themselves.
    try {
      await taskPalletLights(id, "off", { actor: currentActor() });
    } catch (_) { /* never blocks closing */ }
    onClose({ lightsOff: true });
  }

  return (
    <Modal show onHide={() => onClose({ lightsOff: false })} centered style={{ zIndex: 2000 }}
      aria-labelledby="pallet-beacons-title" data-testid="pallet-beacons-modal">
      <Modal.Header closeButton>
        <div>
          <Modal.Title id="pallet-beacons-title">{PALLET_BEACONS_TITLE}</Modal.Title>
          <div className="small text-muted" data-testid="pallet-beacons-subline">{subline}</div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {pallets.map((p) => (
          <PalletRow
            key={p.pallet_id}
            pallet={p}
            busy={busy}
            error={rowErrors[p.pallet_id]}
            note={freeBeaconNote(p, { free_note: rowNotes[p.pallet_id] })}
            refusalNote={rowNotes[p.pallet_id] || ""}
            onSwap={() => beaconAction(p, "swap")}
            onAssign={() => beaconAction(p, "assign")}
            onFind={() => findOne(p)}
          />
        ))}
        {error && <div className="alert alert-warning py-2 mt-2 mb-0 small" role="status">{error}</div>}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <Button variant="outline-secondary" onClick={again} disabled={!!busy || !anyBeacon}>
          {busy === "again" ? "Lighting…" : "Light again"}
        </Button>
        <Button variant="primary" onClick={ok} disabled={busy === "off"}>
          {busy === "off" ? "Turning the lights off…" : "OK"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export function PalletRow({ pallet: p, busy, error, note, refusalNote, onSwap, onAssign, onFind, onMove, station = "Laser" }) {
  const kind = palletRowKind(p, station);
  const pill = palletRowPill(kind);
  const [showMap, setShowMap] = useState(false);
  // A new find object only for a new command: useFindProgress starts over on a new object.
  const find = useMemo(
    () => (p.command_id ? { command_id: p.command_id, beacon_id: p.beacon_id, progress: null } : null),
    [p.command_id, p.beacon_id],
  );
  const progress = useFindProgress(find);
  const light = kind === "no_free" ? { text: "" } : palletLightWords(p, progress);
  const failed = !!(progress && (progress.phase === "failed" || progress.phase === "expired"));
  const parts = palletPartsText(p);
  const location = palletRowLocation(p, progress);
  const caption = location ? locationCaption(location, Date.now()) : "";
  // the map only when there is something to draw on it (a zone answer, a dock or a point)
  const mappable = !!(location && ((location.zone && location.zone.name) || hasPoint(location)));

  return (
    <div style={styles.row} data-testid="pallet-beacon-row" data-kind={kind}>
      <div style={styles.rowHead}>
        <span style={{ ...styles.pill, ...PILL_COLORS[pill] }} data-testid="pallet-beacon-pill">{pill}</span>
        <div style={styles.text}>
          <span>{palletRowText(p, station)}</span>
          {kind === "label" && station === "Laser" && (
            <>
              {" "}
              <button type="button" className="btn btn-link btn-sm" style={styles.linkBtn} onClick={onAssign}
                disabled={!!busy}>
                {busy === `assign:${p.pallet_id}` ? "Putting a beacon on…" : "Put a beacon on it"}
              </button>
            </>
          )}
        </div>
      </div>
      <div style={styles.under}>
        {parts.length > 0 && <div style={styles.parts}>{parts.join(", ")}</div>}
        {light.text && (
          <div className={`text-${light.tone}`} style={styles.light} role="status" data-testid="pallet-beacon-light">
            {light.busy && <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />}
            {light.text}
            {failed && (
              <button type="button" className="btn btn-link btn-sm ms-2" style={styles.linkBtn} onClick={onFind}
                disabled={!!busy}>Try again</button>
            )}
          </div>
        )}
        {caption && !showMap && (
          <div style={styles.where} data-testid="pallet-beacon-where">{caption}</div>
        )}
        {showMap && location && (
          <BeaconLocation className="mt-1" beaconId={p.beacon_id} location={location} />
        )}
        <div style={styles.buttons}>
          {palletRowCanFind(p) && (
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={onFind} disabled={!!busy}
              data-testid="pallet-beacon-find">
              {busy === `find:${p.pallet_id}` ? "Lighting…" : `Find ${p.beacon_id}`}
            </button>
          )}
          {mappable && (
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowMap((v) => !v)}
              aria-expanded={showMap} data-testid="pallet-beacon-map">
              {showMap ? "Hide map" : "Show map"}
            </button>
          )}
          {p.holding && onMove && (
            // a holding pallet at a station after the laser: combine / split it (HoldingMoveDialog, 2026-10-29)
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onMove} disabled={!!busy}
              data-testid="pallet-beacon-move">
              Move pieces
            </button>
          )}
          {kind === "new" && (
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onSwap} disabled={!!busy}>
              {busy === `swap:${p.pallet_id}` ? "Picking another…" : "Use a different beacon"}
            </button>
          )}
          {kind === "no_free" && (
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={onAssign} disabled={!!busy}
              data-testid="pallet-beacon-retry">
              {busy === `assign:${p.pallet_id}` ? "Looking for a beacon…" : "Try again"}
            </button>
          )}
        </div>
        {p.find_note && (
          // why a Find left it alone (another instruction on its screen, the laser still filling it)
          <div className="small text-warning mt-1" data-testid="pallet-beacon-find-note">{p.find_note}</div>
        )}
        {(kind === "no_free" ? note : refusalNote) && (
          // why no beacon is free: on a no-free row always; on any other row after a refused swap / assign
          <div className="small text-muted mt-1" data-testid="pallet-beacon-free-note">{kind === "no_free" ? note : refusalNote}</div>
        )}
        {error && <div className="small text-danger mt-1" role="alert">{error}</div>}
      </div>
    </div>
  );
}
